import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';
import { syncCartItems } from '../../services/productService';
import { clearStoredUserSession, saveAuthenticatedProfile } from '../../utils/authStorage';
import './LoginRequiredModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const extractApiMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (typeof data?.message === 'string') {
    return data.message;
  }

  if (typeof data?.errors === 'string') {
    return data.errors;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat().find(Boolean);
    if (firstError) {
      return firstError;
    }
  }

  return fallback;
};

const getUserIdFromResponse = (data) => {
  const user = data?.data;

  if (!user || typeof user === 'string') {
    return null;
  }

  return (
    user.user_id ||
    user.id ||
    user.customer_id ||
    user.user?.user_id ||
    user.user?.id ||
    null
  );
};

const fetchAuthenticatedUser = async (token) => {
  const { data } = await axios.get(`${API_BASE_URL}/auth/get-user-details`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      token,
    },
  });

  return data?.data || {};
};

const getStoredCartItems = () => {
  try {
    return Object.values(JSON.parse(localStorage.getItem('carts') || '{}'));
  } catch (error) {
    return [];
  }
};

export default function LoginRequiredModal() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('register');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);

  const trimmedName = name.trim();
  const normalizedMobile = mobile.replace(/\D/g, '');

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
      setStep('register');
      setVerificationCode('');
    };

    window.addEventListener('open-login-modal', handleOpen);

    return () => {
      window.removeEventListener('open-login-modal', handleOpen);
    };
  }, []);

  const handleClose = () => {
    if (loading) {
      return;
    }

    setIsOpen(false);
    setStep('register');
    setVerificationCode('');
  };

  const buildGenerateOtpFormData = () => {
    const formData = new FormData();
    formData.append('name', trimmedName);
    formData.append('mobile', normalizedMobile);
    return formData;
  };

  const buildVerifyOtpFormData = () => {
    const formData = new FormData();
    formData.append('mobile', normalizedMobile);
    formData.append('otp', verificationCode);
    return formData;
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    if (!trimmedName) {
      toast.info('Please enter your name');
      return;
    }

    if (!normalizedMobile) {
      toast.info('Please enter mobile number');
      return;
    }

    if (normalizedMobile.length !== 10) {
      toast.info('Please enter a valid 10 digit mobile number');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/generate-otp`,
        buildGenerateOtpFormData(),
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (data?.status === false) {
        toast.error(data?.message || 'Failed sending OTP');
        return;
      }

      localStorage.setItem('auth_mobile', normalizedMobile);
      localStorage.setItem('auth_name', trimmedName);
      toast.success(data?.message || 'OTP sent successfully');
      setStep('otp');
      setVerificationCode('');
    } catch (error) {
      toast.error(extractApiMessage(error, 'Failed sending OTP'));
      console.log('Failed Sending OTP', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOtp = async (event) => {
    event.preventDefault();

    if (!verificationCode) {
      toast.info('Please enter OTP');
      return;
    }

    if (verificationCode.length !== 4) {
      toast.info('Please enter a valid 4 digit OTP');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/verify-otp`,
        buildVerifyOtpFormData(),
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (data?.status === false) {
        toast.error(data?.message || 'OTP verification failed');
        return;
      }

      const token = data?.token;
      const verifiedMobile =
        typeof data?.data === 'string' ? data.data : data?.data?.mobile;
      const userId = getUserIdFromResponse(data);

      if (!token) {
        toast.error('OTP verified, but login token was missing');
        return;
      }

      clearStoredUserSession({ preserveCart: true });
      localStorage.setItem('token', token);
      localStorage.setItem('auth_mobile', verifiedMobile || normalizedMobile);
      localStorage.setItem('auth_name', trimmedName);

      let resolvedUserId = userId;
      try {
        const authenticatedUser = await fetchAuthenticatedUser(token);
        resolvedUserId =
          authenticatedUser?.id ||
          authenticatedUser?.user_id ||
          authenticatedUser?.customer_id ||
          userId;

        saveAuthenticatedProfile(authenticatedUser, {
          name: trimmedName,
          mobile: verifiedMobile || normalizedMobile,
          userId,
        });
      } catch (error) {
        console.error('Failed fetching authenticated user details', error);
      }

      if (resolvedUserId) {
        localStorage.setItem('user_id', String(resolvedUserId));
      }

      const storedCartItems = getStoredCartItems();

      if (resolvedUserId && storedCartItems.length > 0) {
        try {
          await syncCartItems({ user_id: resolvedUserId, items: storedCartItems });
        } catch (error) {
          console.error('Failed syncing guest cart after login', error);
          toast.error('Login successful, but cart sync failed');
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['auth-user-details'] });
      await queryClient.invalidateQueries({ queryKey: ['get-cart'] });

      toast.success(data?.message || 'OTP verified successfully');
      window.dispatchEvent(new CustomEvent('login-modal-authenticated'));
      window.dispatchEvent(new CustomEvent('auth-state-changed'));
      setIsOpen(false);
      setStep('register');
      setVerificationCode('');
    } catch (error) {
      toast.error(extractApiMessage(error, 'OTP verification failed'));
      console.log('Registration Failed ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMobile = () => {
    setStep('register');
    setVerificationCode('');
  };

  return (
    <>
      <ToastContainer />
      {isOpen && (
        <div
          className="login-required-backdrop"
          onClick={handleClose}
          aria-label="Login required"
        >
          <div
            className="login-required-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="login-required-close"
              onClick={handleClose}
              aria-label="Close login prompt"
              disabled={loading}
            >
              x
            </button>
            <div
              className="login-section-inner"
              style={{
                backgroundImage: 'url("/kuku-assets/images/login backside.png")',
              }}
            >
              <div className="login-required-grid">
                <div className="login-required-visual" aria-hidden="true" />
                <div className="login-required-panel">
                  <div className="login-required-card">
                    {step === 'register' && (
                      <>
                        <div className="login-required-heading">
                          <h4>Let's Get Started!</h4>
                          <p>Register using your mobile number &amp; explore more.</p>
                        </div>

                        <form onSubmit={handleRegister}>
                          <div className="login-required-floating-field">
                            <input
                              id="login-required-name"
                              type="text"
                              value={name}
                              onChange={(event) => setName(event.target.value)}
                              placeholder=" "
                              autoComplete="name"
                            />
                            <label htmlFor="login-required-name">
                              Your Name <sup>*</sup>
                            </label>
                          </div>

                          <div className="login-required-floating-field">
                            <input
                              id="login-required-mobile"
                              type="tel"
                              inputMode="numeric"
                              maxLength={10}
                              value={mobile}
                              onChange={(event) =>
                                setMobile(event.target.value.replace(/\D/g, '').slice(0, 10))
                              }
                              placeholder=" "
                              autoComplete="tel"
                            />
                            <label htmlFor="login-required-mobile">
                              Your Mobile <sup>*</sup>
                            </label>
                          </div>

                          <button
                            type="submit"
                            className="login-required-submit"
                            disabled={loading}
                          >
                            {loading ? 'Sending...' : 'Get OTP'}
                          </button>
                        </form>
                      </>
                    )}

                    {step === 'otp' && (
                      <>
                        <div className="login-required-heading">
                          <h4>OTP Verification</h4>
                          <p>We have sent a code to {normalizedMobile}</p>
                        </div>

                        <form onSubmit={handleSubmitOtp}>
                          <div className="login-required-floating-field">
                            <input
                              id="login-required-otp"
                              type="tel"
                              inputMode="numeric"
                              maxLength={4}
                              value={verificationCode}
                              onChange={(event) =>
                                setVerificationCode(
                                  event.target.value.replace(/\D/g, '').slice(0, 4)
                                )
                              }
                              placeholder=" "
                              autoComplete="one-time-code"
                            />
                            <label htmlFor="login-required-otp">
                              Enter OTP <sup>*</sup>
                            </label>
                          </div>

                          <button
                            type="submit"
                            className="login-required-submit"
                            disabled={loading}
                          >
                            {loading ? 'Verifying...' : 'Submit OTP'}
                          </button>
                        </form>

                        <div className="login-required-otp-actions">
                          <button type="button" onClick={handleChangeMobile}>
                            Change mobile
                          </button>
                          <button
                            type="button"
                            onClick={handleRegister}
                            disabled={loading}
                          >
                            Resend OTP
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
